import { type JSX, useMemo, useState } from 'react';
import { Navigate, generatePath, useNavigate } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { getAssessmentSubjectDetails, getOrganizationDetails } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { ExceptionsListing, ExceptionsListingProps } from 'Components/Exceptions/ExceptionsListing/ExceptionsListing';
import { useFileManagementArea } from 'Components/Files/FileManagementArea';
import { IssuesListing, IssuesListingProps } from 'Components/Issues/IssuesListing/IssuesListing';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { AssessmentsListing } from 'Components/UcrmTprm/AssessmentsListing/AssessmentsListing';
import { AdvertiseDueDiligenceAiAnalysisModal, ConfirmDueDiligenceAiAnalysisModal } from 'Components/UcrmTprm/DueDiligenceAiAnalysisModal/DueDiligenceAiAnalysisModal';
import { ReviewForm } from 'Components/UcrmTprm/ReviewForm/ReviewForm';
import reviewFormStyles from 'Components/UcrmTprm/ReviewForm/ReviewForm.module.css';
import { ICON_COPY, ICON_SAVE, ICON_SUBMIT } from 'Config/Icons';
import { TPRM_FINAL_REVIEW, TPRM_FINAL_REVIEW_HISTORICAL, TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_FINAL_REVIEW_HISTORICAL, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getSummitGuideDueDiligenceFeature } from 'Helpers/UcrmTprm';
import { AssessmentSubjectArchiveBase, AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState, DueDiligenceTimeframes } from 'Models/DueDiligenceWorkflow';
import { ExceptionBreadcrumbSource, ExceptionHistoryModificationsResponse, ExceptionResponse } from 'Models/Exceptions';
import { IssueBreadcrumbSource, IssueHistoryModificationsResponse, IssueResponse } from 'Models/Issues';
import { OrganizationArchiveBase, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { TprmOrUcrmDueDiligenceAssessment, getArchivedAssessmentSubject, getArchivedOrganization } from 'Models/UcrmTprm';

import { DueDiligenceFinalReviewSubmitConfirmationModal } from './Components/DueDiligenceFinalReviewSubmitConfirmationModal/DueDiligenceFinalReviewSubmitConfirmationModal';

export interface DueDiligenceFinalReviewLoaderProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export const DueDiligenceFinalReviewLoader = (props: DueDiligenceFinalReviewLoaderProps) => {
    return (
        <PageDataLoader
            path={TPRM_FINAL_REVIEW}
            fetchData={(urlParams) => Promise.all([getOrganizationDetails(props.moduleApi, urlParams.third_party_id), getAssessmentSubjectDetails(props.moduleApi, urlParams.third_party_id, urlParams.service_id), props.assessmentApi.getAllAssessmentsForAssessmentSubject(urlParams.third_party_id, urlParams.service_id), props.dueDiligenceAssessmentConfigurationApi.getTimeframes()])}
            renderForm={(result) => {
                const assessmentSubject = result[1].data;
                // If the workflow is complete, the current Final Review is read-only, and the true source of that data is the archived (historical) record. Redirect there. `last_assessed` is guaranteed to equal the latest archive's timestamp.
                if (assessmentSubject.assessment_workflow_data.state !== AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE && assessmentSubject.last_assessed !== undefined) {
                    return <Navigate to={generatePath(props.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW_HISTORICAL : TPRM_FINAL_REVIEW_HISTORICAL, { third_party_id: assessmentSubject.vendor_id, service_id: assessmentSubject.id, timestamp: assessmentSubject.last_assessed })} replace />;
                }
                return <DueDiligenceFinalReview artificialIntelligenceApi={props.artificialIntelligenceApi} assessments={result[2].data} authHandler={props.authHandler} documentApi={props.documentApi} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} exceptions={result[0].data.exceptions} issues={result[0].data.issues} module={props.module} assessmentSubject={assessmentSubject} organization={result[0].data} timeframes={result[3].data} moduleApi={props.moduleApi} />;
            }}
        />
    );
};

export interface DueDiligenceFinalReviewHistoricalLoaderProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export const DueDiligenceFinalReviewHistoricalLoader = (props: DueDiligenceFinalReviewHistoricalLoaderProps) => {
    return (
        <PageDataLoader
            path={TPRM_FINAL_REVIEW_HISTORICAL}
            fetchData={(urlParams) => Promise.all([props.assessmentApi.getDueDiligenceWorkflowArchive(urlParams.third_party_id, urlParams.service_id, urlParams.timestamp), props.dueDiligenceAssessmentConfigurationApi.getTimeframes()])}
            renderForm={(result) => {
                const historicalData = result[0].data;
                const timeframes = result[1].data;
                return <DueDiligenceFinalReview artificialIntelligenceApi={props.artificialIntelligenceApi} assessments={historicalData.assessments} authHandler={props.authHandler} documentApi={props.documentApi} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} exceptions={historicalData.exceptions} issues={historicalData.issues} module={props.module} assessmentSubject={getArchivedAssessmentSubject(props.module, historicalData)} organization={getArchivedOrganization(props.module, historicalData)} timeframes={timeframes} moduleApi={props.moduleApi} historical={{ timestamp: historicalData.timestamp }} />;
            }}
        />
    );
};

interface DueDiligenceFinalReviewProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentSubject: AssessmentSubjectBase | AssessmentSubjectArchiveBase;
    assessments: TprmOrUcrmDueDiligenceAssessment[];
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    exceptions: ExceptionResponse[] | ExceptionHistoryModificationsResponse[];
    issues: IssueResponse[] | IssueHistoryModificationsResponse[];
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    organization: OrganizationResponseBase | OrganizationArchiveBase;
    timeframes: DueDiligenceTimeframes;

    historical?: { timestamp: string };
}

/**
 * The page for the Final Review step of an assessment subject's due diligence workflow (TPRM or UCRM, per `module`). Displays these sections:
 * * Assessments (a table summarizing all due diligence assessments)
 * * Issues (a table of all issues mapped to the organization)
 * * Exceptions (a table of all exceptions mapped to the organization)
 * * Final Review (the main form where the user, among other things, selects a residual risk)
 */
const DueDiligenceFinalReview = (props: DueDiligenceFinalReviewProps): JSX.Element => {
    const navigate = useNavigate();
    const cachedData = useCachedData();
    const summitGuideAvailability = cachedData.checkAdvancedFeatureAvailability(getSummitGuideDueDiligenceFeature(props.module));

    const userId = useMemo(() => props.authHandler.getAuthenticatedUserSubject()!, [props.authHandler]);
    const userIsAdmin = useMemo(() => props.authHandler.getUserRoles().includes(Role.ADMIN), [props.authHandler]);

    const [showSummitGuideModal, setShowSummitGuideModal] = useState(false);

    // Stored in state because the assessment subject will be updated if the user manually requests an AI analysis or submits the Final Review, in which case we want to display updated state.
    const [assessmentSubject, setAssessmentSubject] = useState<AssessmentSubjectBase | AssessmentSubjectArchiveBase>(props.assessmentSubject);

    // A completed workflow's Final Review is served exclusively by the historical URL (see `DueDiligenceFinalReviewHistoricalLoader`); the current URL redirects there on load or after submit. So this page is historical only when explicitly loaded as such.
    const historicalInfo = props.historical;
    const reviewFormDisabled = historicalInfo !== undefined;

    const [reviewFormState, setReviewFormState] = useState({
        additionalInformation: assessmentSubject.assessment_workflow_data.final_review_human_assessment.comments,
        overallEffectiveness: assessmentSubject.assessment_workflow_data.final_review_human_assessment.effectiveness,
        residualRisk: assessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk,
    });

    const [submitReviewWithManagedFiles, reviewFileManagementHookValues, , resetFiles] = useFileManagementArea<AssessmentSubjectBase>(props.documentApi, assessmentSubject.assessment_workflow_data.final_review_human_assessment.files);

    const [reviewApiRequestState, setReviewApiRequestState] = useState<'none' | 'saving' | 'submitting'>('none');
    const [showConfirmCopyAssessmentReviewModal, setShowConfirmCopyAssessmentReviewModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string>();
    const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'failure'; message: string }>();

    /** A button that allows the user to copy all values from a single due diligence assessment's Review step to the Final Review. Shows only if there is a single assessment and the Final Review is not historical. */
    const copySingleAssessmentReviewButton =
        props.assessments.length === 1 && !reviewFormDisabled ? (
            <Button variant="linkText" size="sm" onClick={() => setShowConfirmCopyAssessmentReviewModal(true)}>
                Copy assessment review to final review
            </Button>
        ) : undefined;

    const validateAndShowSubmitModal = () => {
        if (reviewFormState.residualRisk === undefined || reviewFormState.residualRisk === RiskRating.UNRATED) {
            setValidationErrorMessage('Residual risk is required.');
        } else {
            setValidationErrorMessage(undefined);
            setShowSubmitModal(true);
        }
    };

    const handleReviewSubmit = async (isSubmit: boolean, newAssessmentDueDate?: Date, newIrqTargetCompletionDate?: Date) => {
        setReviewApiRequestState(isSubmit ? 'submitting' : 'saving');
        setToastMessage(undefined);
        try {
            const updatedAssessmentSubject = await submitReviewWithManagedFiles(async (fileUpdates) => {
                return (
                    await props.moduleApi.saveOrSubmitFinalReview(props.organization.id, assessmentSubject.id, {
                        is_submit: isSubmit,
                        file_updates: fileUpdates,
                        comments: reviewFormState.additionalInformation,
                        new_assessment_due_date: newAssessmentDueDate ? jsDateToIso8601(newAssessmentDueDate) : undefined,
                        new_irq_target_completion_date: newIrqTargetCompletionDate ? jsDateToIso8601(newIrqTargetCompletionDate) : undefined,
                        overall_control_effectiveness: reviewFormState.overallEffectiveness,
                        residual_risk: reviewFormState.residualRisk,
                    })
                ).data;
            });

            if (isSubmit) {
                // After a successful submit, the Final Review is now historical; redirect so the URL and refreshed data reflect the archived record. Without doing this, even if state was updated to reflect a historical state, the user would experience errors after refreshing the page (including after being logged out due to inactivity before logging back in) or navigating back to the page.
                navigate(generatePath(props.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW_HISTORICAL : TPRM_FINAL_REVIEW_HISTORICAL, { third_party_id: props.organization.id, service_id: assessmentSubject.id, timestamp: updatedAssessmentSubject.last_assessed! }), { replace: true });
            } else {
                setAssessmentSubject(updatedAssessmentSubject);
                setToastMessage({ type: 'success', message: 'Final review saved.' });
            }
        } catch (error) {
            // A submit is performed from within the confirmation modal, which displays its own success/failure banner, so let the error propagate to it. A save is performed directly on the page, so surface the error here.
            if (isSubmit) {
                throw error;
            }
            setToastMessage({ type: 'failure', message: error.message });
        } finally {
            setReviewApiRequestState('none');
        }
    };

    const issuesListingProps: IssuesListingProps = {
        type: props.module,
        issues: props.issues,
        documentApi: props.documentApi,
        historical: historicalInfo !== undefined,
        createButtonVariant: 'secondaryButton',
        createIssueOrigin: {
            source: IssueBreadcrumbSource.FINAL_REVIEW,
            module: props.module,
            organizationId: props.organization.id,
            assessmentSubjectId: assessmentSubject.id,
            assessmentSubjectDisplayName: `${props.organization.name} - ${assessmentSubject.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: props.organization.id,
        preselectedOrganizationIdForFilter: props.organization.id,
        organizations: [props.organization],
        useHorizontalRuleBelowTitle: true,
        hideCreateButton: historicalInfo !== undefined,
    };

    const exceptionsListingProps: ExceptionsListingProps = {
        type: props.module,
        exceptions: props.exceptions,
        documentApi: props.documentApi,
        historical: historicalInfo !== undefined,
        createButtonVariant: 'secondaryButton',
        createExceptionOrigin: {
            source: ExceptionBreadcrumbSource.FINAL_REVIEW,
            module: props.module,
            organizationId: props.organization.id,
            assessmentSubjectId: assessmentSubject.id,
            assessmentSubjectDisplayName: `${props.organization.name} - ${assessmentSubject.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: props.organization.id,
        preselectedOrganizationIdForFilter: props.organization.id,
        organizations: [props.organization],
        useHorizontalRuleBelowTitle: true,
        hideCreateButton: historicalInfo !== undefined,
    };

    return (
        <>
            {toastMessage && <TextToast variant={toastMessage.type} clearToast={() => setToastMessage(undefined)} text={toastMessage.message} />}
            {historicalInfo !== undefined && <Alert type="warning">This final review is from a historical due diligence workflow and cannot be edited.</Alert>}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={props.module === ProductModule.UCRM ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING}>{props.module}</BreadcrumbLink>
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: props.organization.id, service_id: assessmentSubject.id })}>{`${props.organization.name} - ${assessmentSubject.name}`}</BreadcrumbLink>
                        <BreadcrumbText>Final Review</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Final Review"
                headerDescription={`Perform a final review of all assessments conducted for ${props.organization.name} - ${assessmentSubject.name}.`}
                body={[
                    {
                        title: 'Assessments',
                        content:
                            // If we're viewing a current Final Review and shared due diligence assessments are being used, the assessment subject has no assessments of its own, and we can safely link to the parent.
                            // If we're viewing a historical Final Review where shared due diligence assessments were used, we can list them (assessments are duplicated into the historical record), but we cannot safely link to the parent (it may have since been deleted).
                            props.assessmentSubject.assessment_workflow_setup.common_assessment_parent && !props.historical ? (
                                <>
                                    <Text>{`This ${props.module === ProductModule.UCRM ? 'use case' : 'third-party service'} is using shared due diligence assessments provided by ${props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.name}:`}</Text>
                                    <Link to={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.vendor_id, service_id: props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.id })} size="lg">
                                        {props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.name}
                                    </Link>
                                </>
                            ) : (
                                <>
                                    {props.historical !== undefined && props.assessmentSubject.assessment_workflow_setup.common_assessment_parent && <Text>These shared due diligence assessments were provided by {props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.name}</Text>}
                                    <AssessmentsListing assessments={props.assessments} module={props.module} pageVariant="standard" historical={historicalInfo} />
                                </>
                            ),
                    },
                    {
                        content: <IssuesListing {...issuesListingProps} />,
                    },
                    {
                        content: <ExceptionsListing {...exceptionsListingProps} />,
                    },
                    {
                        title: 'Final Review',
                        buttons: copySingleAssessmentReviewButton,
                        content: (
                            <>
                                {validationErrorMessage && <Alert type="error">{validationErrorMessage}</Alert>}
                                <ReviewForm
                                    _type="FinalReview"
                                    artificialIntelligenceApi={props.artificialIntelligenceApi}
                                    module={props.module}
                                    onAiError={(message) => setToastMessage({ type: 'failure', message })}
                                    disabled={reviewFormDisabled}
                                    documentApi={props.documentApi}
                                    inherentRisk={assessmentSubject.assessment_workflow_data.irq_inherent_risk_score ?? RiskRating.UNRATED}
                                    additionalInformation={reviewFormState.additionalInformation}
                                    overallEffectiveness={reviewFormState.overallEffectiveness}
                                    residualRisk={reviewFormState.residualRisk}
                                    savedOverallEffectiveness={assessmentSubject.assessment_workflow_data.final_review_human_assessment.effectiveness}
                                    savedResidualRisk={assessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk}
                                    fileManagementHookValues={reviewFileManagementHookValues}
                                    onAdditionalInformationChange={(value) => setReviewFormState((prev) => ({ ...prev, additionalInformation: value || undefined }))}
                                    onOverallEffectivenessChange={(value) => setReviewFormState((prev) => ({ ...prev, overallEffectiveness: value }))}
                                    onResidualRiskChange={(value) => {
                                        setReviewFormState((prev) => ({ ...prev, residualRisk: value }));
                                        setValidationErrorMessage(undefined);
                                    }}
                                    aiReview={assessmentSubject.assessment_workflow_data.final_review_ai_assessment}
                                    aiReviewMetadata={assessmentSubject.assessment_workflow_data.final_review_ai_assessment_metadata}
                                    onClickSummitGuideButton={() => setShowSummitGuideModal(true)}
                                />
                                <div className={reviewFormStyles.saveButtons}>
                                    <Button variant="secondary" onClick={() => handleReviewSubmit(false)} fontAwesomeImage={ICON_SAVE} isLoading={reviewApiRequestState === 'saving'} loadingText="Saving..." disabled={reviewFormDisabled}>
                                        Save Final Review
                                    </Button>
                                    {(userIsAdmin || userId === assessmentSubject.vendor_service_manager_user_id) && (
                                        <Button variant="primary" onClick={validateAndShowSubmitModal} fontAwesomeImage={ICON_SUBMIT} disabled={reviewFormDisabled}>
                                            Submit Final Review
                                        </Button>
                                    )}
                                </div>
                            </>
                        ),
                    },
                ]}
            />
            {showConfirmCopyAssessmentReviewModal && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
                    headerText="Copy Assessment Review to Final Review"
                    buttonText="Copy"
                    buttonIcon={ICON_COPY}
                    buttonLoadingText="Copying..."
                    areYouSureText={`Are you sure you want to copy the Review from the "${props.assessments[0].name}" assessment to the Final Review?`}
                    hideModal={() => setShowConfirmCopyAssessmentReviewModal(false)}
                    performOperation={async () => {
                        const updatedAssessmentSubject = (await props.moduleApi.copyAssessmentReviewToFinalReview(props.organization.id, assessmentSubject.id)).data;
                        setAssessmentSubject(updatedAssessmentSubject);
                        setReviewFormState({
                            additionalInformation: updatedAssessmentSubject.assessment_workflow_data.final_review_human_assessment.comments,
                            overallEffectiveness: updatedAssessmentSubject.assessment_workflow_data.final_review_human_assessment.effectiveness,
                            residualRisk: updatedAssessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk,
                        });
                        resetFiles(updatedAssessmentSubject.assessment_workflow_data.final_review_human_assessment.files);
                        return 'Review values copied.';
                    }}
                >
                    <Text>All values in the Final Review form will be overwritten. This may be useful when there is a single due diligence assessment and no further work needs to be performed before submitting the Final Review.</Text>
                </ConfirmationModal>
            )}
            {showSubmitModal && (
                <DueDiligenceFinalReviewSubmitConfirmationModal
                    assessments={props.assessments}
                    dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi}
                    hideModal={() => {
                        setShowSubmitModal(false);
                    }}
                    inherentRiskScore={assessmentSubject.inherent_risk_score ?? RiskRating.UNRATED}
                    module={props.module}
                    onSubmit={async (newAssessmentDueDate, newIrqTargetCompletionDate) => {
                        await handleReviewSubmit(true, newAssessmentDueDate, newIrqTargetCompletionDate);
                    }}
                    assessmentSubject={assessmentSubject}
                    timeframes={props.timeframes}
                />
            )}
            {showSummitGuideModal && (summitGuideAvailability.isAvailable ? <ConfirmDueDiligenceAiAnalysisModal type="FinalReview" allAssessmentsSubmitted={props.assessments.every((assessment) => assessment.assessment_submitted_timestamp !== undefined)} artificialIntelligenceApi={props.artificialIntelligenceApi} hideModal={() => setShowSummitGuideModal(false)} module={props.module} assessmentSubject={assessmentSubject} onFinalReviewAnalysisRequested={setAssessmentSubject} /> : <AdvertiseDueDiligenceAiAnalysisModal hideModal={() => setShowSummitGuideModal(false)} module={props.module} reasonUnavailable={summitGuideAvailability.reason} />)}
        </>
    );
};
